import { LocalStorageKeys } from '@constants/enums';
import { RoleIds } from '@efexteam/permission-manager';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { captureException } from '@sentry/react';
import {
  UserPreferencesKeys,
  UserPreferencesResponse,
} from '../../../hooks/useUserPreferences/useUserPreferences.types';
import authSvc from '../../../services/auth/authSvc';
import { UserProfileResponse } from '../../../services/auth/types';
import { aesDecryptCBC } from '../../../utils/crypt';
import { LoadState } from '../../store/types';
import userPreferencesConstants from '../../../hooks/useUserPreferences/useUserPreferences.constants';
import storageService from '../../../services/auth/storageService';

type ProfileState = LoadState<
  UserProfileResponse & {
    userPreferences?: UserPreferencesResponse;
  }
>;

export const initialState: ProfileState = {
  error: '',
  loading: false,
  data: null,
};

const fetchUserProfile = createAsyncThunk('fetchUserProfile', async () => {
  const userProfile = await authSvc.getUserProfile();

  let response: UserPreferencesResponse = [];

  try {
    response = await authSvc.getUserPreferences(userProfile.uuid);
  } catch (error) {
    response = userPreferencesConstants.DEFAULT_USER_PREFERENCES;
    captureException(error);
  }

  const userPreferences: UserPreferencesResponse = response;

  const existingPreferenceKeys = userPreferences.map(
    (preference) => preference.key
  );

  const missingPreferences = Object.keys(UserPreferencesKeys).filter(
    (prefKey) => !existingPreferenceKeys.includes(prefKey)
  );

  if (missingPreferences.length === 0) {
    return { userProfile, userPreferences };
  }

  return { userProfile, userPreferences };
});

export const userProfileSlice = createSlice({
  name: 'userProfile',
  initialState,
  reducers: {
    clearProfile: (state) => {
      Object.assign(state, initialState);
    },
    updateUserPreference: (state, action) => {
      if (state.data && state.data.userPreferences) {
        const { preference, value } = action.payload;

        const preferenceExists = state.data.userPreferences.some(
          (pref: { key: string }) => pref.key === preference
        );

        if (preferenceExists) {
          state.data.userPreferences = state.data.userPreferences.map(
            (pref: {
              key: string;
              value: string | boolean;
              created_at: string;
              updated_at: string;
            }) => {
              if (pref.key === preference) {
                return { ...pref, value, updated_at: new Date().toISOString() };
              }
              return pref;
            }
          );
        } else {
          const now = new Date().toISOString();
          state.data.userPreferences.push({
            key: preference,
            value,
            created_at: now,
            updated_at: now,
          });
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUserProfile.pending, (state) => {
      state.loading = true;
      state.error = '';
      state.data = null;
    });
    builder.addCase(
      fetchUserProfile.fulfilled,
      (state, { payload: { userProfile, userPreferences } }) => {
        localStorage.setItem(
          LocalStorageKeys.IS_KYC,
          String(userProfile.isKyc)
        );

        if (userProfile.cognitoUuid) {
          storageService.setCognitoUuid(userProfile.cognitoUuid);
        }
        state.loading = false;
        const data = {
          ...userProfile,
          legalRepresentative: {
            ...userProfile.legalRepresentative,
            email: aesDecryptCBC(userProfile.legalRepresentative.email || ''),
            phoneNumber: aesDecryptCBC(
              userProfile.legalRepresentative.phoneNumber || ''
            ),
          },
          bankAccount: {
            ...userProfile.bankAccount,
            clabe: aesDecryptCBC(userProfile.bankAccount.clabe || ''),
            accountNumber: aesDecryptCBC(
              userProfile.bankAccount.accountNumber || ''
            ),
            routingNumber: aesDecryptCBC(
              userProfile.bankAccount.routingNumber || ''
            ),
          },
          naturalPerson: {
            ...(userProfile.naturalPerson || {}),
            email: userProfile.naturalPerson
              ? aesDecryptCBC(userProfile.naturalPerson.email || '')
              : '',
          },
          intercomHMAC: aesDecryptCBC(userProfile.intercomHMAC),
          userPreferences,
        };
        state.data = data;
        if (
          import.meta.env.VITE_REACT_APP_ENV === 'local_' ||
          userProfile.legalRepresentative.roleId === RoleIds.READ_ONLY
        ) {
          state.data.isFullRegister = false;
          state.data.isKyc = false;
          state.data.is_email_confirmed = false;
        }
        if (import.meta.env.VITE_REACT_APP_ENV === 'local') {
          state.data.featurePayment = true;
          state.data.canUserWithdraw = true;
          state.data.isFullRegister = true;
          state.data.isKyc = true;
          state.data.is_email_confirmed = true;
          state.data.isAvailableChinaPayment = true;
          // state.data.company.isActivePaymentLink = false;
          // state.data.isAwActive = true;
        }
        state.error = '';
      }
    );

    builder.addCase(fetchUserProfile.rejected, (state) => {
      state.loading = false;
      state.data = null;
      state.error = 'ERROR';
    });
  },
});

export const { clearProfile, updateUserPreference } = userProfileSlice.actions;
export { fetchUserProfile };
export default userProfileSlice.reducer;
